import React, { useCallback, useEffect, useState } from 'react';
import { ActionIcon, Group, Select, SimpleGrid, Stack, Text } from '@mantine/core';
import { useAppContext } from '../../contexts/AppContext';
import GoogleMapsViewer from '../GoogleMapsViewer';
import { PaneelKop, Sectie } from './bouwstenen';
import VersieTijdlijn from './VersieTijdlijn';

// Hemelsbrede afstand in hele meters. Bestaande stations hebben geen naam of
// nummer, dus de afstand tot het nieuwe station is het enige onderscheidende.
function afstandMeter(lat1, lng1, lat2, lng2) {
  const R = 6378137;
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLng = (lng2 - lng1) * rad;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(a)));
}

export default function ProjectSidebar() {
  const {
    selectedProject, setSelectedProject,
    handleProjectFieldChange,
    buurtOptions,
    stationstypeOptions,
    kleurstellingOptions,
    typeOptions,
    fetchWithAuth, addToast,
    actieveVersieId, versieRefresh, bumpVersieRefresh,
    ververBuurtVervangingen,
  } = useAppContext();

  // Type/kleur zijn per versie: laad de versies zodat we de uitvoering van de
  // geselecteerde (of nieuwste) versie kunnen tonen en bewerken.
  const [versies, setVersies] = useState([]);
  const projectnummer = selectedProject?.projectnummer;
  const laadVersies = useCallback(async () => {
    if (!projectnummer) { setVersies([]); return; }
    try {
      const res = await fetchWithAuth(`/api/projecten/${encodeURIComponent(projectnummer)}/versies`);
      const data = await res.json();
      if (data.success) setVersies(data.versies);
    } catch (e) { /* velden vallen terug op projectwaarden */ }
  }, [projectnummer, fetchWithAuth]);
  useEffect(() => { laadVersies(); }, [laadVersies, versieRefresh]);

  // Bestaande stations die dit (vervangende) station vervangt.
  const [vervangt, setVervangt] = useState([]);
  const isVervangend = /vervangen/i.test(String(selectedProject?.type || ''));
  const laadVervangt = useCallback(async () => {
    if (!projectnummer || !isVervangend) { setVervangt([]); return; }
    try {
      const res = await fetchWithAuth(`/api/projecten/${encodeURIComponent(projectnummer)}/vervangt`);
      const data = await res.json();
      if (data.success) setVervangt(data.stations);
    } catch (e) { /* sectie blijft leeg */ }
  }, [projectnummer, isVervangend, fetchWithAuth]);
  useEffect(() => { laadVervangt(); }, [laadVervangt]);

  const ontkoppel = async (stationId) => {
    setVervangt(prev => prev.filter(s => s.station_id !== stationId));
    try {
      const res = await fetchWithAuth(
        `/api/projecten/${encodeURIComponent(projectnummer)}/vervangt/${stationId}`,
        { method: 'DELETE' },
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      ververBuurtVervangingen && ververBuurtVervangingen();
    } catch (e) {
      addToast && addToast('Ontkoppelen mislukt', 'error');
      laadVervangt();
    }
  };

  if (!selectedProject) return null;

  // Alleen stationsopgaven in de lijst; de huidige waarde er hoe dan ook bij.
  const typeWerkOpties = Array.from(new Set(
    [...(typeOptions || []), selectedProject.type].filter(Boolean),
  ));

  // Locatie voor de kaartweergave (lokatie1 -> lat/lng -> geometry -> coordinaten)
  let viewerLng = null, viewerLat = null;
  if (selectedProject.lokatie1) {
    try {
      const arr = JSON.parse(selectedProject.lokatie1);
      if (Array.isArray(arr) && arr.length === 2) { viewerLng = arr[0]; viewerLat = arr[1]; }
    } catch (e) { /* ignore */ }
  }
  if ((viewerLat === null || viewerLng === null) && selectedProject.lat !== undefined && selectedProject.lng !== undefined) {
    viewerLat = selectedProject.lat;
    viewerLng = selectedProject.lng;
  }
  if ((viewerLat === null || viewerLng === null) && selectedProject.geometry && Array.isArray(selectedProject.geometry.coordinates)) {
    viewerLng = selectedProject.geometry.coordinates[0];
    viewerLat = selectedProject.geometry.coordinates[1];
  }
  if ((viewerLat === null || viewerLng === null) && typeof selectedProject.coordinaten === 'string' && selectedProject.coordinaten.includes(';')) {
    const [latStr, lngStr] = selectedProject.coordinaten.split(';');
    const lat = parseFloat(latStr.replace(',', '.'));
    const lng = parseFloat(lngStr.replace(',', '.'));
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) { viewerLat = lat; viewerLng = lng; }
  }

  // Buurtopties: de buurtcode is de unieke sleutel (buurtnamen kunnen dubbel
  // voorkomen, bv. meerdere "Poldergebied Loenersloot"). Mantine eist unieke
  // option-values; op naam koppelen crasht de dropdown. Dubbele namen krijgen
  // de code erachter zodat ze te onderscheiden zijn.
  const uniekeBuurten = Array.from(
    new Map((buurtOptions || []).map(o => [o.code, o])).values()
  );
  const naamAantal = uniekeBuurten.reduce((acc, o) => {
    acc[o.naam] = (acc[o.naam] || 0) + 1;
    return acc;
  }, {});
  const buurtData = uniekeBuurten.map(o => ({
    value: o.code,
    label: naamAantal[o.naam] > 1 ? `${o.naam} (${o.code})` : o.naam,
  }));
  const huidigeBuurtCode = selectedProject.buurtcode
    || uniekeBuurten.find(o => o.naam === selectedProject.buurtnaam)?.code
    || null;

  // De versie waarvan de uitvoering (type/kleur) wordt getoond/bewerkt: de in de
  // tijdlijn geselecteerde versie, anders de nieuwste.
  const nieuwsteVersie = versies.length
    ? versies.reduce((a, b) => (new Date(b.aangemaakt_op) > new Date(a.aangemaakt_op) ? b : a))
    : null;
  const actieveVersie = versies.find(v => v.id === actieveVersieId) || nieuwsteVersie;
  const huidigType = actieveVersie ? (actieveVersie.stationstype ?? null) : (selectedProject.stationstype || null);
  const huidigKleur = actieveVersie ? (actieveVersie.kleurstelling ?? null) : (selectedProject.kleurstelling || null);

  // Uitvoering wijzigen: schrijf één of meer velden (stationstype/kleurstelling)
  // naar de actieve versie (valt terug op het project als er geen versies zijn)
  // en ververs kaart + tijdlijn.
  const wijzigUitvoering = async (updates) => {
    const genormaliseerd = Object.fromEntries(
      Object.entries(updates).map(([k, v]) => [k, v || '']),
    );
    if (!actieveVersie) {
      Object.entries(genormaliseerd).forEach(([veld, waarde]) => handleProjectFieldChange(veld, waarde));
      return;
    }
    setVersies(prev => prev.map(v => (v.id === actieveVersie.id ? { ...v, ...genormaliseerd } : v)));
    try {
      const res = await fetchWithAuth(
        `/api/projecten/${encodeURIComponent(projectnummer)}/versies/${actieveVersie.id}/uiterlijk`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(genormaliseerd) },
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      bumpVersieRefresh();
    } catch (e) {
      addToast && addToast('Opslaan mislukt', 'error');
      laadVersies();
    }
  };

  return (
    <>
      {viewerLat !== null && viewerLng !== null ? (
        <GoogleMapsViewer lat={viewerLat} lng={viewerLng} />
      ) : (
        <div style={{ background: 'var(--mantine-color-gray-3)', height: 120, width: '100%' }} />
      )}
      <PaneelKop
        label="Station"
        titel={selectedProject.projectnummer}
        subtitel={selectedProject.type}
        onSluit={() => setSelectedProject(null)}
      />
      <Stack gap="md" p="md">
        <Sectie titel="Gegevens">
          <Select
            label="Buurt"
            data={buurtData}
            value={huidigeBuurtCode}
            searchable
            onChange={code => {
              const opt = uniekeBuurten.find(o => o.code === code);
              if (!opt) return;
              setSelectedProject({ ...selectedProject, buurtnaam: opt.naam, buurtcode: opt.code });
              handleProjectFieldChange('buurtnaam', opt.naam);
              handleProjectFieldChange('buurtcode', opt.code);
            }}
          />
          <Select
            label="Type werk"
            description="Bepaalt de rand van de stip: ombouw grijs, vervangend station blauw."
            // De lijst bevat alleen stationsopgaven (geen MS-werk of tracé-
            // werkzaamheden). Heeft dit project toch zo'n type uit de import,
            // dan tonen we die waarde er wél bij, anders lijkt het veld leeg.
            data={typeWerkOpties}
            value={selectedProject.type || null}
            searchable
            clearable
            onChange={v => handleProjectFieldChange('type', v || '')}
          />
          <SimpleGrid cols={2} spacing="xs">
            <Select
              label="Stationstype"
              data={stationstypeOptions}
              value={huidigType}
              onChange={v => wijzigUitvoering({ stationstype: v })}
              clearable
            />
            <Select
              label="Kleurstelling"
              data={kleurstellingOptions}
              value={huidigKleur}
              onChange={v => wijzigUitvoering({ kleurstelling: v })}
              clearable
            />
          </SimpleGrid>
          {actieveVersie && (
            <Text size="xs" c="dimmed">
              Type en kleur gelden voor locatie {actieveVersie.versie_nr}
              {actieveVersie.id !== actieveVersieId ? ' (nieuwste)' : ''}. Selecteer een
              andere locatie in de tijdlijn om die aan te passen.
            </Text>
          )}
        </Sectie>

        {isVervangend && (
          <Sectie titel="Te vervangen station(s)">
            {vervangt.length === 0 ? (
              <Text size="xs" c="dimmed">
                Nog geen station gekoppeld. Ga naar buurtniveau en gebruik de knop
                “Te vervangen station koppelen” in de balk links: klik eerst dit
                station, daarna het bestaande station dat vervangen wordt.
              </Text>
            ) : (
              <Stack gap={4}>
                {vervangt.map(s => (
                  <Group key={s.station_id} justify="space-between" wrap="nowrap" gap={6}>
                    <Group gap={6} wrap="nowrap">
                      <span style={{
                        width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                        background: '#888', border: '2px solid #e03131',
                      }} />
                      <Text size="xs">
                        Bestaand station
                        {viewerLat !== null && viewerLng !== null
                          ? ` — ${afstandMeter(viewerLat, viewerLng, s.lat, s.lng)} m`
                          : ''}
                      </Text>
                    </Group>
                    <ActionIcon
                      size="sm" variant="subtle" color="red"
                      aria-label="Koppeling verwijderen"
                      onClick={() => ontkoppel(s.station_id)}
                    >
                      ✕
                    </ActionIcon>
                  </Group>
                ))}
              </Stack>
            )}
          </Sectie>
        )}

        <VersieTijdlijn
          projectnummer={selectedProject.projectnummer}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
        />
      </Stack>
    </>
  );
}
